import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { isSameDay } from '@siat-fe/core';
import { FasciaOraria, withOra } from '../calendar/calendar.utils';
import { HolidaysService } from '../calendar/services/holidays.service';
import { CustomValidators } from './custom-form-validator';

/**
 * Form shape shared by the "insert" and "reschedule" appuntamento flows:
 * a date field plus a separate time field, combined by `dateTimeValidator`.
 */
export interface EventDateTimeFormControls {
  /** The selected date (time-of-day ignored). */
  dateStart: FormControl<Date | null>;
  /** The selected time-of-day (date part ignored). */
  time: FormControl<Date | null>;
}

/**
 * Date+time form with an optional end, used to move an event keeping or changing its end.
 */
export interface EventDateTimeRangeFormControls
  extends EventDateTimeFormControls {
  /** The optional end date (time-of-day ignored). */
  dateEnd: FormControl<Date | null>;
  /** The optional end time-of-day (date part ignored). */
  timeEnd: FormControl<Date | null>;
}

/**
 * Builds the date+time form with a required end, pre-filled with `start` and
 * `end` (the start itself when the event has no duration).
 * @return {FormGroup<EventDateTimeRangeFormControls>} The form
 */
export function buildEventDateTimeRangeForm(
  formBuilder: FormBuilder,
  start: Date | null,
  end: Date | null,
): FormGroup<EventDateTimeRangeFormControls> {
  return formBuilder.group(
    {
      dateStart: new FormControl<Date | null>(start, Validators.required),
      time: new FormControl<Date | null>(start, Validators.required),
      dateEnd: new FormControl<Date | null>(end ?? start, Validators.required),
      timeEnd: new FormControl<Date | null>(end ?? start, Validators.required),
    },
    {
      validators: [CustomValidators.dateTimeValidator(), fineEventoValidator()],
    },
  );
}

/**
 * Checks the end of an event: it can't come before the start.
 * @return {ValidatorFn} Sets `fineNonSuccessiva` on the group
 */
export function fineEventoValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const { dateStart, time, dateEnd, timeEnd } = control.value ?? {};
    if (!dateStart || !time || !dateEnd || !timeEnd) return null;
    const inizio = combineDateAndTime(dateStart, time);
    const fine = combineDateAndTime(dateEnd, timeEnd);
    return fine >= inizio ? null : { fineNonSuccessiva: true };
  };
}

/**
 * Builds the date+time form group used by both the "add appuntamento" and
 * the "reschedule appuntamento" flows.
 * @return {FormGroup<EventDateTimeFormControls>} form pre-filled with `initialDate`.
 */
export function buildEventDateTimeForm(
  formBuilder: FormBuilder,
  initialDate: Date | null,
): FormGroup<EventDateTimeFormControls> {
  return formBuilder.group(
    {
      dateStart: new FormControl<Date | null>(initialDate, Validators.required),
      time: new FormControl<Date | null>(initialDate, Validators.required),
    },
    { validators: CustomValidators.dateTimeValidator() },
  );
}

/**
 * Rounds a date's minutes up to the next 15-minute interval.
 * @return {Date} new Date rounded up to the next quarter-hour, seconds/ms zeroed.
 */
export function roundUpToNext15Min(date: Date): Date {
  const rounded = new Date(date.getTime());
  const minutes = rounded.getMinutes();
  const remainder = minutes % 15;

  if (remainder > 0) {
    rounded.setMinutes(minutes + (15 - remainder));
  }
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);

  return rounded;
}

/**
 * Initial date/time for a NEW appuntamento: the clicked date if it's already
 * in the future, otherwise now — rounded up to the next 15-minute interval.
 * @return {Date} rounded initial Date to prefill the form with.
 */
export function getRoundedInitialDate(initialDate: Date): Date {
  const now = new Date();
  const startDate = initialDate > now ? initialDate : new Date(now);
  return roundUpToNext15Min(startDate);
}

/**
 * Formats a date's hours/minutes as a zero-padded 'HH:mm' string.
 * @return {string} the time portion of `date` as 'HH:mm'.
 */
export function formatHHmm(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Minimum selectable time ('HH:mm') for the given selected date: if the date
 * is today, it's "now" rounded up to the next 15 minutes; otherwise '00:00'.
 * @return {string} minimum selectable 'HH:mm' time, or '' if `selectedDate` is null.
 */
export function computeMinTime(selectedDate: Date | null): string {
  if (!selectedDate) return '';

  const now = new Date();
  if (!isSameDay(selectedDate, now)) return '00:00';

  return formatHHmm(roundUpToNext15Min(new Date(now)));
}

/**
 * Builds a datepicker `dateFilter` that blocks holidays when
 * `blockHolidayInsert` is enabled, shared by every date/time field that
 * needs holiday filtering (single or range).
 * @return {(date: Date | null) => boolean} filter for `[dateFilter]` on a datepicker.
 */
export function buildHolidayFilter(
  holidaysService: HolidaysService,
): (date: Date | null) => boolean {
  return (date: Date | null): boolean =>
    !date ||
    !holidaysService.blockHolidayInsert() ||
    !holidaysService.isHoliday(date);
}

/**
 * Combines the separate date and time form values into a single Date.
 * @return {Date} a Date with `date`'s day and `time`'s hours/minutes.
 */
export function combineDateAndTime(date: Date, time: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
  );
}

/**
 * Combines a date and time into a UTC date+time string for API requests.
 * @return {{date: string; time: string}} `
 *
 */
export function combineToUtcDateAndTime(
  date: Date,
  time: Date,
): { date: string; time: string } {
  const combined = combineDateAndTime(date, time);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return {
    date: `${combined.getUTCFullYear()}-${pad(combined.getUTCMonth() + 1)}-${pad(combined.getUTCDate())}`,
    time: `${pad(combined.getUTCHours())}:${pad(combined.getUTCMinutes())}:${pad(combined.getUTCSeconds())}`,
  };
}

/**
 * Latest of the given 'HH:mm' times, ignoring empty values.
 * @returns {string} The latest time, '' when none is set
 */
export function latestHHmm(...times: (string | null | undefined)[]): string {
  return times
    .filter((time): time is string => !!time)
    .reduce((latest, time) => (time > latest ? time : latest), '');
}

/**
 * Checks that start + duration does not go past the closing time ('HH:mm') of the same day.
 * @returns {ValidatorFn} Sets `fineOltreChiusura` on the group
 */
export function fineEntroChiusuraValidator(
  chiusura: string,
  getDurataMs: (group: AbstractControl) => number | null,
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const { dateStart, time } = group.value ?? {};
    const durataMs = getDurataMs(group);
    if (!dateStart || !time || !durataMs || durataMs <= 0) return null;
    const inizio = combineDateAndTime(dateStart, time);
    const [ore, minuti] = chiusura.split(':').map(Number);
    const limite = new Date(inizio);
    limite.setHours(ore, minuti, 0, 0);
    return inizio.getTime() + durataMs > limite.getTime()
      ? { fineOltreChiusura: { chiusura } }
      : null;
  };
}

/**
 * Last selectable start time for a closing time: the start + duration must end by closing, on 15-minute steps.
 * @returns {string} The 'HH:mm' time, at least one 15-minute step before `chiusura`
 */
export function ultimoInizioHHmm(
  chiusura: string,
  durataMs?: number | null,
): string {
  const [ore, minuti] = chiusura.split(':').map(Number);
  const durataMinuti = Math.max(15, Math.ceil((durataMs ?? 0) / 60000));
  const inizio = new Date(2000, 0, 1, ore, minuti - durataMinuti);
  inizio.setMinutes(Math.floor(inizio.getMinutes() / 15) * 15);
  return formatHHmm(inizio);
}

/**
 * Moves a start back so that start + duration ends by closing, on 15-minute steps, never before opening.
 * @returns {Date} The start unchanged when it already fits or when no start in the window can fit
 */
export function adattaInizioAllaChiusura(
  inizio: Date,
  durataMs: number,
  fascia: Pick<FasciaOraria, 'inizio' | 'fine'>,
): Date {
  const chiusura = withOra(inizio, fascia.fine);
  if (inizio.getTime() + durataMs <= chiusura.getTime()) return inizio;
  const adattato = new Date(chiusura.getTime() - durataMs);
  adattato.setMinutes(Math.floor(adattato.getMinutes() / 15) * 15, 0, 0);
  return adattato >= withOra(inizio, fascia.inizio) ? adattato : inizio;
}
